import { supabaseClient } from "@/lib/supabase";
import { clearSentryUser, setSentryUser } from "@/lib/sentry";
import { useAuth } from "@/utils/auth";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

export async function getAuth() {
    const { data, error } = await supabaseClient.auth.getUser();
    if (error) {
        console.warn("User not logged in");
        return null;
    }
    return data.user;
}

export const authQueryOptions = () => {
    return queryOptions({
        queryKey: ["user"],
        queryFn: () => getAuth(),
        retry: 3,
        staleTime: 3000,
    });
};

function useAuthQuery() {
    const query = useQuery(authQueryOptions());
    const auth = useAuth();

    const { data: user, isSuccess, isError } = query;

    useEffect(() => {
        // Only update auth state after query has completed (success or error)
        if (isSuccess || isError) {
            if (user?.id) {
                auth.login(user.id);
                // Set Sentry user context for error tracking (id only - no email)
                setSentryUser({
                    id: user.id,
                });
            } else {
                auth.logout();
                clearSentryUser();
            }
        }
        // Depend on user?.id (not user) so a refetched User object does not retrigger login/logout loops.
    }, [user?.id, isSuccess, isError, auth.login, auth.logout]);

    return query;
}

export function useGetAuth() {
    const { data: user } = useAuthQuery();
    return user;
}

export default useAuthQuery;
